import { create } from 'zustand';
import { readStored, writeStored } from '@/components/desktop/types';

/**
 * The pages of Settings, and which one is open. Kept apart from the screen so
 * the command palette and Help can open a given page without importing it.
 */
export type SectionId =
  | 'display'
  | 'accessibility'
  | 'windows'
  | 'keyboard'
  | 'dashboard'
  | 'password'
  | 'account'
  | 'company'
  | 'reset'
  | 'about';

export interface SectionDef {
  id: SectionId;
  label: string;
  /** Extra words people may search for that are not in any setting's label. */
  keywords: string;
  /**
   * Hides the page from people without it: it is left out of the list, out of
   * the search and out of the command palette, and opening it by its
   * remembered id falls back to Display.
   */
  permission?: string;
}

export const SECTIONS: SectionDef[] = [
  {
    id: 'display',
    label: 'Display',
    keywords: 'zoom size bigger smaller text font language urdu english',
  },
  {
    id: 'accessibility',
    label: 'Accessibility',
    keywords: 'buttons gloves tablet rows contrast motion animation links eyesight',
  },
  {
    id: 'windows',
    label: 'Windows & layout',
    keywords: 'window monitor screen panes columns layout reset',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    keywords: 'dashboard home cards charts figures hide show order arrange first screen',
  },
  {
    id: 'keyboard',
    label: 'Keyboard',
    keywords: 'shortcut keys keyboard f2 enter escape arrows',
  },
  {
    id: 'password',
    label: 'Password & sign-in',
    keywords: 'password security change sign out devices sessions phone computer',
  },
  {
    id: 'account',
    label: 'My account',
    keywords: 'name user id role farms permissions account',
  },
  {
    id: 'company',
    label: 'Company',
    keywords: 'company name logo address phone tax ntn currency rupees money date time zone format lakh financial year lock print',
    // The Managing Director's and the Super Admin's. These are the company's
    // own details — the name on every printout, the currency, the financial
    // year — not anyone's own settings, so the page is not shown to the rest
    // of the staff at all rather than shown to them greyed out. The server
    // reserves the permission to those two roles; this only keeps the screen
    // honest.
    permission: 'iam.company.update',
  },
  {
    id: 'reset',
    label: 'Reset data',
    keywords: 'reset clear wipe delete remove data start again fresh empty test entries go live',
    permission: 'sys.reset.read',
  },
  {
    id: 'about',
    label: 'About',
    keywords: 'about version build server help',
  },
];

/**
 * The pages this person may see, in list order.
 *
 * The one place the rule lives: the page list, the search and the command
 * palette all ask this rather than each repeating the test, so a page put
 * behind a permission is hidden everywhere at once instead of everywhere
 * somebody remembered.
 */
export function visibleSections(can: (permission: string) => boolean): SectionDef[] {
  return SECTIONS.filter((s) => !s.permission || can(s.permission));
}

const SECTION_KEY = 'poultry.settings.section';

export function storedSection(): SectionId {
  const stored = readStored<string>(SECTION_KEY, 'display');
  return SECTIONS.some((s) => s.id === stored) ? (stored as SectionId) : 'display';
}

/**
 * The open page lives outside the component so the command palette can
 * switch it while Settings is already on screen.
 */
export const useSettingsSection = create<{ section: SectionId | null }>(() => ({ section: null }));

/** Opens Settings on the given page, from anywhere. */
export function openSettingsPage(id: SectionId, navigate: (screenId: string) => void): void {
  rememberSettingsPage(id);
  navigate('settings');
}

/** Shows `id` now and opens on it next time. */
export function rememberSettingsPage(id: SectionId): void {
  writeStored(SECTION_KEY, id);
  useSettingsSection.setState({ section: id });
}

